import { isoToCalendarDayKey, todayCalendarDayKey } from './assetBookingCalendar';
import type { BookingPeriodKind } from './assetBookingPricingOptions';
import { addBookingPeriods, bookingPeriodCountLabel } from './bookingPeriodEndDate';
import { countBookingPeriodsBetween } from './bookingTotalPriceCompute';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Cushion so backend latency does not reject start_date as in the past. */
export const BOOKING_START_TIME_BUFFER_MS = 5 * 60 * 1000;

export function bufferedStartReferenceNow(now: Date = new Date()): Date {
  return new Date(now.getTime() + BOOKING_START_TIME_BUFFER_MS);
}

export function formatLocalHoursMinutes(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function compareHm(a: string, b: string): number {
  return a.localeCompare(b);
}

export function isLocalCalendarDayToday(
  year: number,
  monthIndex: number,
  day: number,
  now: Date = new Date(),
): boolean {
  return now.getFullYear() === year && now.getMonth() === monthIndex && now.getDate() === day;
}

/** End of calendar day for date-only (non-hourly) bookings. */
export const DATE_ONLY_END_TIME = '23:59';

function defaultTodayStartTime(
  year: number,
  monthIndex: number,
  day: number,
  now: Date = new Date(),
): string {
  const buffered = bufferedStartReferenceNow(now);
  if (!isLocalCalendarDayToday(year, monthIndex, day, buffered)) {
    return DATE_ONLY_END_TIME;
  }
  return formatLocalHoursMinutes(buffered);
}

/**
 * Default start time when the user does not pick an hour.
 * Date-only bookings use start-of-day for future dates; for today, use now + buffer.
 */
export function defaultDateOnlyStartTime(
  year: number,
  monthIndex: number,
  day: number,
  now: Date = new Date(),
): string {
  if (isLocalCalendarDayToday(year, monthIndex, day, now)) {
    return defaultTodayStartTime(year, monthIndex, day, now);
  }
  return '00:00';
}

/** Default start when hourly booking UI is shown but the time field is empty. */
export function defaultHourlyStartTime(
  year: number,
  monthIndex: number,
  day: number,
  now: Date = new Date(),
): string {
  const morning = '08:00';
  if (!isLocalCalendarDayToday(year, monthIndex, day, now)) {
    return morning;
  }
  const todayStart = defaultTodayStartTime(year, monthIndex, day, now);
  return compareHm(todayStart, morning) > 0 ? todayStart : morning;
}

function addHoursToHm(hm: string, hours: number): string {
  const [hh, mm] = hm.split(':').map((x) => parseInt(x, 10));
  const h = Number.isFinite(hh) ? hh : 0;
  const min = Number.isFinite(mm) ? mm : 0;
  const d = new Date(2000, 0, 1, h, min, 0, 0);
  d.setHours(d.getHours() + hours);
  return formatLocalHoursMinutes(d);
}

export function defaultHourlyEndTime(
  year: number,
  monthIndex: number,
  day: number,
  now: Date = new Date(),
): string {
  const afternoon = '15:00';
  if (!isLocalCalendarDayToday(year, monthIndex, day, now)) {
    return afternoon;
  }
  const startDefault = defaultHourlyStartTime(year, monthIndex, day, now);
  const oneHourAfterStart = addHoursToHm(startDefault, 1);
  return compareHm(oneHourAfterStart, afternoon) > 0 ? oneHourAfterStart : afternoon;
}

/** When hourly booking on today, do not keep a time that is before the buffered minimum start. */
export function clampHourlyStartTimeOnToday(
  display: ScheduleDisplay,
  candidateTime: string,
  now: Date = new Date(),
): string {
  const ymd = parseScheduleYmd(display);
  if (!ymd || !/^\d{2}:\d{2}$/.test(candidateTime)) {
    return candidateTime;
  }
  if (!isLocalCalendarDayToday(ymd.y, ymd.m, ymd.d, now)) {
    return candidateTime;
  }
  const minStart = defaultHourlyStartTime(ymd.y, ymd.m, ymd.d, now);
  return compareHm(candidateTime, minStart) < 0 ? minStart : candidateTime;
}

/** Whole hours from `startIso` until local midnight (exclusive). Returns 0 when no full hour fits. */
export function maxHourlyPeriodCountSameDay(startIso: string): number {
  const start = new Date(startIso.trim());
  if (Number.isNaN(start.getTime())) {
    return 0;
  }
  const startOfNextDay = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
  const ms = startOfNextDay.getTime() - start.getTime();
  return Math.max(0, Math.floor(ms / (60 * 60 * 1000)));
}

export function validateBookingCreateSchedule(
  startIso: string,
  endIso: string,
  opts: { hourly: boolean; sameDayHourly?: boolean; now?: Date },
): string | null {
  const start = startIso.trim();
  const end = endIso.trim();
  if (!start) {
    return 'Choose a start date and time.';
  }
  if (!end) {
    return 'Choose an end date and time.';
  }
  const startDt = new Date(start);
  const endDt = new Date(end);
  if (Number.isNaN(startDt.getTime()) || Number.isNaN(endDt.getTime())) {
    return 'Start and end dates must be valid.';
  }
  if (endDt.getTime() <= startDt.getTime()) {
    return 'End must be after start.';
  }
  const now = opts.now ?? new Date();
  if (opts.hourly) {
    if (startDt.getTime() < bufferedStartReferenceNow(now).getTime()) {
      return 'Start time is too soon — choose a time at least a few minutes from now.';
    }
    if (opts.sameDayHourly) {
      const startKey = isoToCalendarDayKey(start);
      const endKey = isoToCalendarDayKey(end);
      if (!startKey || !endKey || startKey !== endKey) {
        return 'Hourly bookings must start and end on the same day.';
      }
      if (maxHourlyPeriodCountSameDay(start) < 1) {
        return 'Start time is too late — not enough time left today for an hourly booking.';
      }
    }
  } else {
    const startKey = isoToCalendarDayKey(start);
    const todayKey = todayCalendarDayKey(now);
    if (!startKey || !todayKey || startKey < todayKey) {
      return 'Start date cannot be in the past.';
    }
  }
  return null;
}

/** Validate PATCH booking — only `end_date` may change; must stay after existing start. */
export function validateBookingUpdateEndDate(startIso: string, endIso: string): string | null {
  const start = startIso.trim();
  const end = endIso.trim();
  if (!end) {
    return 'Choose an end date and time.';
  }
  const startDt = new Date(start);
  const endDt = new Date(end);
  if (!start || Number.isNaN(startDt.getTime())) {
    return 'Booking start date is missing — reload the booking and try again.';
  }
  if (Number.isNaN(endDt.getTime())) {
    return 'End date must be valid.';
  }
  if (endDt.getTime() <= startDt.getTime()) {
    return 'End must be after the booking start.';
  }
  return null;
}

function periodEndToleranceMs(kind: BookingPeriodKind): number {
  return kind === 'hour' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
}

/** Weekly / monthly / yearly rates bill in whole periods — partial spans round up. */
export function bookingPeriodKindRoundsUpCeil(kind: BookingPeriodKind): boolean {
  return kind === 'week' || kind === 'month' || kind === 'year';
}

export type ResolvedBookingPeriodSchedule = {
  periodCount: number;
  alignedEndIso: string;
  roundedUp: boolean;
};

/**
 * Map a calendar start/end to whole billing periods.
 * For week/month/year, when the picked end extends past the last complete period,
 * {@link ResolvedBookingPeriodSchedule.roundedUp} is true and the count is ceiled.
 */
export function resolveBookingPeriodSchedule(
  startIso: string,
  endIso: string,
  kind: BookingPeriodKind,
): ResolvedBookingPeriodSchedule | null {
  const start = startIso.trim();
  const end = endIso.trim();
  if (!start || !end) {
    return null;
  }
  const floorCount = countBookingPeriodsBetween(start, end, kind);
  if (floorCount === null || floorCount < 1) {
    return null;
  }
  const floorEnd = addBookingPeriods(start, kind, floorCount);
  if (!floorEnd) {
    return null;
  }
  if (!bookingPeriodKindRoundsUpCeil(kind)) {
    return { periodCount: floorCount, alignedEndIso: floorEnd, roundedUp: false };
  }
  const tolerance = periodEndToleranceMs(kind);
  const endMs = new Date(end).getTime();
  const floorEndMs = new Date(floorEnd).getTime();
  if (Math.abs(endMs - floorEndMs) <= tolerance) {
    return { periodCount: floorCount, alignedEndIso: floorEnd, roundedUp: false };
  }
  if (endMs <= floorEndMs) {
    return { periodCount: floorCount, alignedEndIso: floorEnd, roundedUp: false };
  }
  const ceilCount = floorCount + 1;
  const alignedEndIso = addBookingPeriods(start, kind, ceilCount);
  if (!alignedEndIso) {
    return null;
  }
  return { periodCount: ceilCount, alignedEndIso, roundedUp: true };
}

/** PATCH booking end must stay on whole periods of the saved pricing option (no arbitrary end datetime). */
export function validateBookingUpdatePeriodSchedule(
  startIso: string,
  endIso: string,
  kind: BookingPeriodKind,
  minPeriodCount: number,
): string | null {
  const base = validateBookingUpdateEndDate(startIso, endIso);
  if (base) {
    return base;
  }
  const start = startIso.trim();
  const end = endIso.trim();
  if (kind === 'hour') {
    const startKey = isoToCalendarDayKey(start);
    const endKey = isoToCalendarDayKey(end);
    if (!startKey || !endKey || startKey !== endKey) {
      return 'Hourly bookings must end on the same day as the start.';
    }
  }
  const count = countBookingPeriodsBetween(start, end, kind);
  if (count === null) {
    return 'End must align with complete booking periods.';
  }
  const floor = Math.max(1, Math.floor(minPeriodCount));
  if (count < floor) {
    return `Cannot shorten below ${bookingPeriodCountLabel(kind, floor)}.`;
  }
  const expectedEnd = addBookingPeriods(start, kind, count);
  if (!expectedEnd) {
    return 'End must align with complete booking periods.';
  }
  const delta = Math.abs(new Date(end).getTime() - new Date(expectedEnd).getTime());
  if (delta > periodEndToleranceMs(kind)) {
    return `End must match the period count (${bookingPeriodCountLabel(kind, count)}) from the booking start.`;
  }
  return null;
}

export function parseScheduleYmd(display: ScheduleDisplay): { y: number; m: number; d: number } | null {
  const y = parseInt(display.y.trim(), 10);
  const m = parseInt(display.m.trim(), 10);
  const d = parseInt(display.d.trim(), 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return null;
  }
  return { y, m, d };
}

/**
 * Derive `end_date` from start + whole billing periods (create/update submit safety net).
 */
export function bookingEndIsoForPeriodCount(
  startIso: string,
  kind: BookingPeriodKind,
  periodCount: number,
): string | null {
  if (!startIso.trim() || periodCount < 1) {
    return null;
  }
  const endIso = addBookingPeriods(startIso.trim(), kind, Math.floor(periodCount));
  return endIso || null;
}

export type ScheduleDisplay = { y: string; m: string; d: string; t: string };

export function resolveBookingStartDefaultTime(
  display: ScheduleDisplay,
  showHourInput: boolean,
  now: Date = new Date(),
): string {
  const ymd = parseScheduleYmd(display);
  if (!ymd) {
    return showHourInput ? '08:00' : '00:00';
  }
  return showHourInput
    ? defaultHourlyStartTime(ymd.y, ymd.m, ymd.d, now)
    : defaultDateOnlyStartTime(ymd.y, ymd.m, ymd.d, now);
}

export function resolveBookingEndDefaultTime(
  display: ScheduleDisplay,
  showHourInput: boolean,
  now: Date = new Date(),
): string {
  if (!showHourInput) {
    return DATE_ONLY_END_TIME;
  }
  const ymd = parseScheduleYmd(display);
  if (!ymd) {
    return '15:00';
  }
  return defaultHourlyEndTime(ymd.y, ymd.m, ymd.d, now);
}
